import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Heading,
  HStack,
  Flex,
  Select,
  Icon,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  FaPlaneDeparture,
  FaHotel,
  FaCarSide,
  FaShip,
  FaTrain,
} from "react-icons/fa";
import Translate from "../LanguageTranslation/Translate";
import axiosInstance from "../../helpers/axiosInstance";
import { useTranslation } from "../LanguageTranslation/TranslationContext";

import FlightTripDetails from "./Tabs/FlightTripDetails";
import AccommodationTripDetails from "./Tabs/AccommodationTripDetails";
import CarTripDetails from "./Tabs/CarTripDetails";
import BoatTripDetails from "./Tabs/BoatTripDetails";
import TrainTripDetails from "./Tabs/TrainTripDetails";

// TripDropdown component (inline for now)
const TripDropdown = ({
  selectedTrip,
  setSelectedTrip,
  setTripName,
  onTripSelect,
  onTripsLoaded,
}) => {
  const { useTranslate } = useTranslation();
  const placeholderText = useTranslate("Select a trip");
  const [trips, setTrips] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get("/bucket-list/");
        const filtered = res.data.data.filter(
          (t) =>
            (t.category || "").toLowerCase() === "itinerary" &&
            t.general_info?.itinerary
        );
        setTrips(filtered);
        onTripsLoaded && onTripsLoaded(filtered.length > 0);
      } catch (err) {
        console.error("Error fetching trips:", err);
        onTripsLoaded && onTripsLoaded(false);
      }
    })();
  }, []);

  const handleChange = (e) => {
    const id = e.target.value;
    const trip = trips.find((t) => t._id === id);
    setSelectedTrip(id);
    setTripName(trip?.general_info?.itinerary?.trip_name || "Unknown Trip");
    onTripSelect(id);
  };

  return (
    <Flex align="center" gap={2}>
      <Select
        placeholder={placeholderText}
        value={selectedTrip}
        onChange={handleChange}
        size="sm"
        borderRadius="xl"
        border="2px solid"
        borderColor="customGreen.500"
        _hover={{ borderColor: "customGreen.500", bg: "rgba(40,99,40,0.08)" }}
        flex="1"
      >
        {trips.map((t) => (
          <option key={t._id} value={t._id}>
            <Translate text={t.general_info.itinerary.trip_name} />
          </option>
        ))}
      </Select>
    </Flex>
  );
};

/* palette */
const BRAND = "#286328";
const BRAND_FILL = "rgba(40,99,40,0.08)";

/* tab meta */
const TRIP_TABS = [
  { value: "flight", label: "Flight", icon: FaPlaneDeparture },
  { value: "accommodation", label: "Accommodation", icon: FaHotel },
  { value: "car", label: "Car", icon: FaCarSide },
  { value: "boat", label: "Boat", icon: FaShip },
  { value: "train", label: "Train", icon: FaTrain },
];

/* single tab button */
const TabBtn = ({ tab, active, onClick, isMobile }) => (
  <Button
    flexShrink={0} /* allow horizontal scrolling */
    minW={isMobile ? "110px" : "16%"} /* wide enough for fingers */
    borderRadius="0"
    py={isMobile ? 4 : 6}
    px={4}
    bg={active ? BRAND_FILL : "transparent"}
    color={active ? BRAND : "black"}
    border={active ? `2px solid ${BRAND}` : "1px solid #d8d8d8"}
    _hover={{ bg: BRAND_FILL }}
    fontWeight="bold"
    fontSize={{ base: "xs", md: "sm" }}
    onClick={onClick}
  >
    <HStack spacing={2} justify="center">
      <Icon as={tab.icon} />
      {/* hide long labels on very narrow phones */}
      <Translate text={tab.label} />
    </HStack>
  </Button>
);

const OffsetTripDetails = ({
  //flights
  tripTab,
  setTripTab,
  tripMode,
  setTripMode,
  tripType,
  setTripType,
  cabinTab,
  setCabinTab,
  passengers,
  setPassengers,
  from,
  setFrom,
  to,
  setTo,
  counts,
  setCounts,
  result,
  setResult,

  //accomadation

  accomCountry,
  setAccomCountry,
  accomType,
  setAccomType,
  accomRooms,
  setAccomRooms,
  accomNights,
  setAccomNights,
  accomResult,
  setAccomResult,

  //cars
  carVehicleType,
  setCarVehicleType,
  carDistance,
  setCarDistance,
  carFuelType,
  setCarFuelType,
  carFuelConsumed,
  setCarFuelConsumed,
  carResult,
  setCarResult,

  //boats
  boatType,
  setBoatType,
  boatDays,
  setBoatDays,
  boatPeople,
  setBoatPeople,
  boatResult,
  setBoatResult,

  //trains
  trainTripMode,
  setTrainTripMode,
  trainTripType,
  setTrainTripType,
  trainPassengers,
  setTrainPassengers,
  trainClass,
  setTrainClass,
  trainFrom,
  setTrainFrom,
  trainTo,
  setTrainTo,
  trainCounts,
  setTrainCounts,
  trainSwap,
  trainResult,
  setTrainResult,

  selectedTrip,
  setSelectedTrip,
  tripName,
  setTripName,
}) => {
  /* true below md → we use scroll overflow rather than equal widths */
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [hasTrips, setHasTrips] = useState(false);

  return (
    <Box flex="2" bg="white" p={6} borderRadius="lg" boxShadow="md">
      <Box mb={8} w="50%">
        <Heading size="md" mb={2}>
          <Translate text="Trip Details" />
        </Heading>
        <TripDropdown
          selectedTrip={selectedTrip}
          setSelectedTrip={setSelectedTrip}
          setTripName={setTripName}
          onTripSelect={() => {}}
          onTripsLoaded={setHasTrips}
        />
      </Box>
      {/* ───────── TAB BAR ───────── */}
      <Box
        /* on phones show a scrollable row, hide scrollbar for iOS/Android */
        overflowX={isMobile ? "auto" : "visible"}
        mb={8}
        css={{
          "::-webkit-scrollbar": { display: "none" },
          "-ms-overflow-style": "none",
          scrollbarWidth: "none",
        }}
      >
        <HStack
          spacing={0}
          w={isMobile ? "max-content" : "full"}
          minW={isMobile ? "max-content" : "full"}
        >
          {TRIP_TABS.map((tab) => (
            <TabBtn
              key={tab.value}
              tab={tab}
              active={tripTab === tab.value}
              onClick={() => setTripTab(tab.value)}
              isMobile={isMobile}
            />
          ))}
        </HStack>
      </Box>

      {/* ───────── TAB PANELS ───────── */}
      {tripTab === "flight" && (
        <FlightTripDetails
          tripTab={tripTab}
          setTripTab={setTripTab}
          tripMode={tripMode}
          setTripMode={setTripMode}
          tripType={tripType}
          setTripType={setTripType}
          cabinTab={cabinTab}
          setCabinTab={setCabinTab}
          passengers={passengers}
          setPassengers={setPassengers}
          from={from}
          setFrom={setFrom}
          to={to}
          setTo={setTo}
          counts={counts}
          setCounts={setCounts}
          result={result}
          setResult={setResult}
          selectedTrip={selectedTrip}
          hasTrips={hasTrips}
        />
      )}
      {tripTab === "accommodation" && (
        <AccommodationTripDetails
          accomCountry={accomCountry}
          setAccomCountry={setAccomCountry}
          accomType={accomType}
          setAccomType={setAccomType}
          accomRooms={accomRooms}
          setAccomRooms={setAccomRooms}
          accomNights={accomNights}
          setAccomNights={setAccomNights}
          accomResult={accomResult}
          setAccomResult={setAccomResult}
          selectedTrip={selectedTrip}
          hasTrips={hasTrips}
        />
      )}
      {tripTab === "car" && (
        <CarTripDetails
          carVehicleType={carVehicleType}
          setCarVehicleType={setCarVehicleType}
          carDistance={carDistance}
          setCarDistance={setCarDistance}
          carFuelType={carFuelType}
          setCarFuelType={setCarFuelType}
          carFuelConsumed={carFuelConsumed}
          setCarFuelConsumed={setCarFuelConsumed}
          carResult={carResult}
          setCarResult={setCarResult}
          selectedTrip={selectedTrip}
          hasTrips={hasTrips}
        />
      )}
      {tripTab === "boat" && (
        <BoatTripDetails
          boatType={boatType}
          setBoatType={setBoatType}
          boatDays={boatDays}
          setBoatDays={setBoatDays}
          boatPeople={boatPeople}
          setBoatPeople={setBoatPeople}
          boatResult={boatResult}
          setBoatResult={setBoatResult}
          selectedTrip={selectedTrip}
          hasTrips={hasTrips}
        />
      )}
      {tripTab === "train" && (
        <TrainTripDetails
          tripMode={trainTripMode}
          setTripMode={setTrainTripMode}
          tripType={trainTripType}
          setTripType={setTrainTripType}
          passengers={trainPassengers}
          setPassengers={setTrainPassengers}
          trainClass={trainClass}
          setTrainClass={setTrainClass}
          from={trainFrom}
          setFrom={setTrainFrom}
          to={trainTo}
          setTo={setTrainTo}
          counts={trainCounts}
          setCounts={setTrainCounts}
          swap={trainSwap}
          trainResult={trainResult}
          setTrainResult={setTrainResult}
          selectedTrip={selectedTrip}
          hasTrips={hasTrips}
        />
      )}
    </Box>
  );
};

export default OffsetTripDetails;
